import { useCallback, useEffect } from 'react'
import {
  AuthenticateWithRedirectCallback,
  SignIn,
  SignUp,
  useAuth as useClerkSession,
  useClerk,
  useUser,
} from '@clerk/clerk-react'
import type { AuthContext } from './dev'
import { useAuthStore, type AuthUser, type UserRole } from './store'

/**
 * OAuth/SSO return handler. Clerk redirects back here after Google; this
 * component consumes the handshake (incl. dev URL session-sync token) and
 * finalizes the session, then routes into the app. Without a dedicated
 * callback route the return can bounce through protected redirects and hang.
 */
export function ClerkSsoCallback() {
  return (
    <AuthenticateWithRedirectCallback
      signInFallbackRedirectUrl="/overview"
      signUpFallbackRedirectUrl="/overview"
    />
  )
}

/** Keeps the Zustand store in sync with Clerk's short-lived session JWT. */
export function ClerkTokenSync() {
  const { isSignedIn, isLoaded, getToken } = useClerkSession()
  const { user: clerkUser } = useUser()
  const setAuth = useAuthStore((s) => s.setAuth)
  const clearAuth = useAuthStore((s) => s.clearAuth)

  const syncToken = useCallback(async () => {
    if (!isLoaded) return

    if (!isSignedIn || !clerkUser) {
      clearAuth()
      return
    }

    try {
      // Use the 'attribution' JWT template so the token carries the user's
      // roles claim (from Clerk public_metadata). Fall back to the default
      // session token if the template isn't available yet — in that case the
      // API applies its CLERK_DEFAULT_ROLES (viewer) fallback.
      let token: string | null = null
      try {
        token = await getToken({ template: 'attribution' })
      } catch {
        token = await getToken()
      }
      if (!token) return

      const roles = (clerkUser.publicMetadata?.roles as string[] | undefined) ?? []
      const role = roles.includes('admin') ? 'admin' : 'viewer'

      const authUser: AuthUser = {
        username:
          clerkUser.primaryEmailAddress?.emailAddress ??
          clerkUser.username ??
          clerkUser.id,
        role,
        deployment_id: undefined,
        brand_ids: undefined,
      }
      setAuth(token, authUser)
    } catch (err) {
      console.error('Failed to sync Clerk token', err)
    }
  }, [isLoaded, isSignedIn, clerkUser, getToken, setAuth, clearAuth])

  useEffect(() => {
    void syncToken()
  }, [syncToken])

  useEffect(() => {
    if (!isSignedIn) return

    const interval = window.setInterval(() => void syncToken(), 30_000)
    const onFocus = () => void syncToken()
    window.addEventListener('focus', onFocus)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
    }
  }, [isSignedIn, syncToken])

  return null
}

export function ClerkSignInPanel() {
  return (
    <div className="flex min-h-[24rem] w-full max-w-md items-center justify-center">
      <SignIn routing="hash" signUpUrl="/sign-up" fallbackRedirectUrl="/overview" />
    </div>
  )
}

export function ClerkSignUpPanel() {
  return (
    <div className="flex min-h-[24rem] w-full max-w-md items-center justify-center">
      <SignUp routing="hash" signInUrl="/login" fallbackRedirectUrl="/overview" />
    </div>
  )
}

export function useClerkAuth(): AuthContext {
  const { isSignedIn, isLoaded } = useClerkSession()
  const { signOut } = useClerk()
  const token = useAuthStore((s) => s.token)
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clearAuth)

  const login = useCallback(async (_username: string, _role: UserRole) => {
    // Clerk SignIn UI handles authentication; no programmatic login needed.
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    clearAuth()
  }, [signOut, clearAuth])

  return {
    user,
    token,
    isAuthenticated: isSignedIn === true,
    isLoading: !isLoaded,
    isAdmin: user?.role === 'admin',
    login,
    logout,
  }
}
